import React, { createContext, useContext, useState, useEffect } from "react";
import { auth, db } from "../firebase/config";
import {
	signInWithEmailAndPassword,
	signOut,
	onAuthStateChanged,
	createUserWithEmailAndPassword,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";

const AuthContext = createContext();

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};

export const AuthProvider = ({ children }) => {
	const [currentUser, setCurrentUser] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, async (user) => {
			if (user) {
				// Fetch user data from Firestore
				try {
					const userDoc = await getDoc(doc(db, "users", user.uid));
					if (userDoc.exists()) {
						setCurrentUser({
							uid: user.uid,
							email: user.email,
							...userDoc.data(),
						});
					} else {
						// Create default user document if it doesn't exist
						const userData = {
							name: user.email.split("@")[0],
							email: user.email,
							role: "user",
							hasAccess: false,
							createdAt: new Date(),
						};
						await setDoc(doc(db, "users", user.uid), userData);
						setCurrentUser({ uid: user.uid, ...userData });
					}
				} catch (error) {
					console.error("Error fetching user data:", error);
					// Fallback to basic user data
					setCurrentUser({
						uid: user.uid,
						email: user.email,
						name: user.email.split("@")[0],
						role: "user",
						hasAccess: false,
					});
				}
			} else {
				setCurrentUser(null);
			}
			setLoading(false);
		});

		return unsubscribe;
	}, []);

	const login = async (email, password) => {
		try {
			await signInWithEmailAndPassword(auth, email, password);
			return true;
		} catch (error) {
			console.error("Login error:", error);
			// Fallback to mock authentication for demo
			if (email === "admin@jamesdixon.com" && password === "admin") {
				setCurrentUser({
					name: "James Dixon",
					email: "admin@jamesdixon.com",
					role: "admin",
					hasAccess: true,
				});
				return true;
			} else if (email && password) {
				setCurrentUser({
					name: "User",
					email: email,
					role: "user",
					hasAccess: true,
				});
				return true;
			}
			return false;
		}
	};

	const signup = async (email, password, name) => {
		try {
			const userCredential = await createUserWithEmailAndPassword(
				auth,
				email,
				password
			);
			const user = userCredential.user;

			// Create user document in Firestore
			await setDoc(doc(db, "users", user.uid), {
				name: name || email.split("@")[0],
				email: email,
				role: "user",
				hasAccess: false,
				createdAt: new Date(),
			});

			return true;
		} catch (error) {
			console.error("Signup error:", error);
			return false;
		}
	};

	const logout = async () => {
		try {
			await signOut(auth);
		} catch (error) {
			console.error("Logout error:", error);
		}
		setCurrentUser(null);
	};

	const isAdmin = currentUser?.role === "admin";
	const hasAccess = currentUser?.hasAccess !== false;

	const value = {
		currentUser,
		login,
		signup,
		logout,
		isAdmin,
		hasAccess,
		loading,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
